import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  createBanner,
  deleteBanner,
  fetchBanners,
  updateBanner,
  uploadBannerImage,
} from '../services/bannersApi';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';

function normalizeForm(banner) {
  return {
    title: banner?.title || '',
    description: banner?.description || '',
    linkUrl: banner?.linkUrl || '',
    displayOrder: banner?.displayOrder != null ? String(banner.displayOrder) : '0',
    durationSeconds: banner?.durationSeconds != null ? String(banner.durationSeconds) : '5',
    status: banner?.status || 'active',
  };
}

const DEEP_LINK_SECTIONS = [
  {
    title: 'Configured entry points',
    items: [
      { value: 'gottea:///path' },
      { value: 'gottea://r/<REFERRAL_CODE>' },
      { value: 'gottea:///r/<REFERRAL_CODE>' },
      { value: 'https://app.gottea.in/r/<REFERRAL_CODE>', note: 'iOS Universal Links' },
      { value: 'exp+got-tea://...', note: 'dev builds only' },
    ],
  },
  {
    title: 'Referral formats',
    items: [
      { value: '/r/<REFERRAL_CODE>' },
      { value: 'https://app.gottea.in/r/<REFERRAL_CODE>' },
      { value: 'gottea://r/<REFERRAL_CODE>' },
      { value: 'gottea:///r/<REFERRAL_CODE>' },
      { value: 'gottea-referral:<REFERRAL_CODE>', note: 'clipboard fallback, not recommended for banner links' },
    ],
  },
  {
    title: 'Routable paths for banner linkUrl',
    items: [
      { value: '/' },
      { value: '/welcome' },
      { value: '/login' },
      { value: '/signup' },
      { value: '/onboarding' },
      { value: '/permissions' },
      { value: '/profile' },
      { value: '/rewards' },
      { value: '/stores' },
      { value: '/stores/<id>' },
      { value: '/orders' },
      { value: '/orders/<id>' },
      { value: '/referrals' },
      { value: '/all-referrals' },
      { value: '/all-rewards' },
      { value: '/shop' },
      { value: '/streak' },
      { value: '/token-ledger' },
      { value: '/account-settings' },
      { value: '/korean-hat-camera' },
    ],
  },
];

const DEEP_LINK_CAVEATS = [
  'Auth-protected paths may redirect based on sign-in and registration state.',
  'Android currently has custom scheme handling, but verified https://app.gottea.in/... App Links are not configured in the checked native manifest.',
  'app.json has a stale gotteaapp scheme, but the app build source of truth is app.config.ts with gottea.',
];

function DeepLinkInfoModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-3xl max-h-[65vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <h3 className="text-xl font-bold">Banner Deep Link Options</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close deep link info">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          {DEEP_LINK_SECTIONS.map((section) => (
            <section key={section.title}>
              <h4 className="font-semibold text-gray-900 mb-3">{section.title}</h4>
              <ul className="space-y-2">
                {section.items.map((item) => (
                  <li key={item.value} className="flex flex-col gap-1 sm:flex-row sm:items-start">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm break-all">{item.value}</code>
                    {item.note && <span className="text-sm text-gray-500 sm:pt-1">{item.note}</span>}
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <section>
            <h4 className="font-semibold text-gray-900 mb-3">Caveats</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
              {DEEP_LINK_CAVEATS.map((caveat) => (
                <li key={caveat}>{caveat}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function BannerForm({ currentBanner, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => normalizeForm(currentBanner));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(currentBanner?.imageUrl || '');
  const [showDeepLinkInfo, setShowDeepLinkInfo] = useState(false);

  useEffect(() => {
    setForm(normalizeForm(currentBanner));
    setImageFile(null);
    setImagePreviewUrl(currentBanner?.imageUrl || '');
  }, [currentBanner]);

  useEffect(() => {
    if (!imageFile) return undefined;
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setImageFile(file);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!currentBanner && !imageFile) {
      // require image at create time
      alert('Please choose a banner image.');
      return;
    }
    const durationSeconds = Number.parseFloat(form.durationSeconds);
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
      alert('Duration must be a positive number of seconds.');
      return;
    }
    onSubmit({
      banner: {
        title: form.title,
        description: form.description,
        linkUrl: form.linkUrl,
        displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
        durationSeconds,
        status: form.status,
      },
      imageFile,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
      {showDeepLinkInfo && <DeepLinkInfoModal onClose={() => setShowDeepLinkInfo(false)} />}
      <div className="bg-white rounded shadow-lg w-full max-w-2xl max-h-svh overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{currentBanner ? 'Edit Banner' : 'Add Banner'}</h3>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block mb-2 font-medium">Banner Image (recommended 691×221, ~3:1)</label>
            <div className="flex flex-col md:flex-row gap-4 md:items-center">
              <div
                className="bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0 border"
                style={{ width: 240, height: Math.round(240 * (221 / 691)) }}
              >
                {imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt="Banner preview" className="w-full h-full object-cover" />
                ) : (
                  <PhotoIcon className="size-10 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  className="w-full border px-3 py-2 rounded"
                  onChange={handleImageChange}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Image is saved when you save the banner.
                </p>
                {imageFile && <p className="text-sm text-teal-600 mt-1">{imageFile.name}</p>}
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">Title</label>
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Internal title (not shown to user)"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Description</label>
            <textarea
              className="w-full border px-3 py-2 rounded min-h-20"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Internal notes (optional)"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="font-medium" htmlFor="banner-link-url">Link URL (optional)</label>
              <button
                type="button"
                onClick={() => setShowDeepLinkInfo(true)}
                className="text-teal-500 hover:text-teal-700"
                aria-label="Show deep link options"
                title="Show deep link options"
              >
                <InformationCircleIcon className="size-5" />
              </button>
            </div>
            <input
              id="banner-link-url"
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={form.linkUrl}
              onChange={(e) => handleChange('linkUrl', e.target.value)}
              placeholder="https://... or a deep link path"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 font-medium">Display Order</label>
              <input
                type="number"
                className="w-full border px-3 py-2 rounded"
                value={form.displayOrder}
                onChange={(e) => handleChange('displayOrder', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first.</p>
            </div>
            <div>
              <label className="block mb-1 font-medium">Duration (seconds)</label>
              <input
                type="number"
                min="0"
                step="any"
                className="w-full border px-3 py-2 rounded"
                value={form.durationSeconds}
                onChange={(e) => handleChange('durationSeconds', e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Must be greater than zero.</p>
            </div>
            <div>
              <label className="block mb-1 font-medium">Status</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="border px-4 py-2 rounded hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Saving...' : imageFile ? 'Save Banner & Image' : 'Save Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageUploadingId, setImageUploadingId] = useState(null);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const result = await fetchBanners();
      setBanners(result);
    } catch (e) {
      setAlertMessage(e.message || 'Failed to load banners.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBanners(); }, []);

  const startCreate = () => { setCurrentBanner(null); setShowForm(true); };
  const startEdit = (b) => { setCurrentBanner(b); setShowForm(true); };

  const handleSubmit = async ({ banner: payload, imageFile }) => {
    setSaving(true);
    try {
      let saved;
      if (currentBanner) {
        saved = await updateBanner(currentBanner.id, payload);
      } else {
        saved = await createBanner(payload);
      }
      if (imageFile) {
        await uploadBannerImage(saved.id, imageFile);
      }
      setAlertMessage(imageFile ? 'Banner and image saved.' : 'Banner saved.');
      setShowForm(false);
      setCurrentBanner(null);
      await loadBanners();
    } catch (e) {
      setAlertMessage(e.message || 'Failed to save banner.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (banner) => {
    setConfirmMessage(`Delete banner "${banner.title || banner.id}"? This cannot be undone.`);
    setConfirmCallback(() => async () => {
      try {
        await deleteBanner(banner.id);
        setConfirmMessage(null);
        setAlertMessage('Banner deleted.');
        await loadBanners();
      } catch (e) {
        setAlertMessage(e.message || 'Failed to delete banner.');
      }
    });
  };

  const handleImageUpload = async (banner, file) => {
    if (!file) return;
    setImageUploadingId(banner.id);
    try {
      await uploadBannerImage(banner.id, file);
      setAlertMessage('Banner image uploaded.');
      await loadBanners();
    } catch (e) {
      setAlertMessage(e.message || 'Failed to upload banner image.');
    } finally {
      setImageUploadingId(null);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Promo Banners</h2>
          <p className="text-gray-500 mt-1">Manage the promo carousel shown on the app home screen.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadBanners}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <ArrowPathIcon className="size-5" />
            Refresh
          </button>
          <PermissionGate permission="content.banners.create"><button
            type="button"
            onClick={startCreate}
            className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <PlusIcon className="size-5" />
            Add Banner
          </button></PermissionGate>
        </div>
      </div>

      {showForm && (
        <BannerForm
          currentBanner={currentBanner}
          saving={saving}
          onCancel={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading banners...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Banner</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Link</th>
                <th className="py-2 px-4 border-b">Order</th>
                <th className="py-2 px-4 border-b">Duration</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Status</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((banner) => (
                <tr key={banner.id}>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-3 min-w-64">
                      <div
                        className="bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0"
                        style={{ width: 160, height: Math.round(160 * (221 / 691)) }}
                      >
                        {banner.imageUrl ? (
                          <img src={banner.imageUrl} alt={banner.title} className="w-full h-full object-cover" />
                        ) : (
                          <PhotoIcon className="size-7 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold">{banner.title || <span className="text-gray-400">Untitled</span>}</div>
                        {banner.description && (
                          <div className="text-sm text-gray-500 line-clamp-2">{banner.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">
                    {banner.linkUrl ? (
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm break-all">{banner.linkUrl}</code>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 border-b">{banner.displayOrder}</td>
                  <td className="py-3 px-4 border-b">{banner.durationSeconds || 5}s</td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">
                    <span className={`px-2 py-1 rounded text-sm ${
                      banner.status === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {banner.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <PermissionGate permission="content.banners.upload"><label className="text-teal-500 hover:opacity-70 cursor-pointer">
                        <PhotoIcon className="size-7" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={imageUploadingId === banner.id}
                          onChange={(e) => {
                            handleImageUpload(banner, e.target.files[0]);
                            e.target.value = '';
                          }}
                        />
                      </label></PermissionGate>
                      <PermissionGate permission="content.banners.update"><button type="button" onClick={() => startEdit(banner)} className="text-teal-500 hover:opacity-70">
                        <PencilSquareIcon className="size-7" />
                      </button></PermissionGate>
                      <PermissionGate permission="content.banners.delete"><button type="button" onClick={() => handleDelete(banner)} className="text-red-500 hover:opacity-70">
                        <TrashIcon className="size-7" />
                      </button></PermissionGate>
                    </div>
                    {imageUploadingId === banner.id && (
                      <div className="text-xs text-gray-500 mt-1">Uploading...</div>
                    )}
                  </td>
                </tr>
              ))}
              {banners.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8">No banners yet. Add one to get started.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Banners;
